/* eslint-disable @typescript-eslint/naming-convention -- x */
import type { IconButtonProps } from '@ori-ui/components';
import { Counter } from '@ori-ui/components';
import type { InputProps } from '@ori-ui/mui';
import { styled } from '@ori-ui/mui';
import type { FC } from 'react';

import { useQuantityPicker } from '~/hooks/useQuantityPicker';
import type { HandleQuantityChangeOptions, TestIdProp } from '~/models';

interface QuantityPickerProps extends TestIdProp {
  canBeDeleted: boolean;
  canBeModified: boolean;
  canBeReduced: boolean;
  initialQuantity: number;
  itemCode: string;
  lineNumber: number;
  maxQuantity: number;
  onQuantityChange: (options: HandleQuantityChangeOptions) => Promise<void>;
}

const Root = styled('div')(({ theme }) => ({
  alignItems: 'center',
  alignSelf: 'flex-end',
  display: 'flex',
  position: 'relative',
  width: 125,

  [theme.breakpoints.up('md')]: {
    alignSelf: 'center',
    flexShrink: 0,
  },
}));

export const QuantityPicker: FC<QuantityPickerProps> = ({
  'data-testid': testId,
  canBeDeleted,
  canBeModified,
  canBeReduced,
  initialQuantity,
  itemCode,
  lineNumber,
  maxQuantity,
  onQuantityChange,
}) => {
  const { inputRef, handleBlur, handleChange, handleFocus, handleKeyDown, value } =
    useQuantityPicker({
      canBeDeleted,
      initialQuantity,
      itemCode,
      lineNumber,
      maxQuantity,
      onQuantityChange,
    });

  return (
    <Root>
      <Counter
        DecreaseIconButtonProps={
          {
            'data-testid': `${testId}-decrease`,
          } as Partial<IconButtonProps>
        }
        disabled={!canBeModified}
        IncreaseIconButtonProps={
          {
            'data-testid': `${testId}-increase`,
          } as Partial<IconButtonProps>
        }
        InputProps={
          {
            type: 'tel',
            inputRef,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
          } as Partial<InputProps>
        }
        maxValue={maxQuantity}
        minValue={canBeReduced ? 0 : initialQuantity}
        value={value}
        onChange={handleChange}
      />
    </Root>
  );
};
